// src/pages/EditService.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ServiceFormFields from "../components/ServiceFormFields";
import {
  MAX_SERVICE_IMAGES,
  createEmptyImageSlots,
  createImageSlotsFromExisting,
  extractImageUrl,
} from "../utils/serviceImageSlots";
import { useNavigate, useParams } from "react-router-dom";

const createEmptyCreneau = () => ({ date: "", heure: "" });
const LOCKED_FIELDS_WHEN_RESERVED = [
  "typePrestation",
  "nombrePlaces",
  "dateService",
  "heure",
  "duree",
  "typeCours",
  "publicCible",
  "prerequis",
  "materiel",
  "accessiblePMR",
  "pmrDetails",
  "lieu",
  "creditsProposes",
  "dateAConvenir",
];

const EditService = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // ← ID du service à modifier

 const [formData, setFormData] = useState({
  titre: "",
  description: "",
  typePrestation: "",
  nombrePlaces: "",
  ville: "",
  adresse: "",
  dateService: [],
  heure: "",
  duree: "",
  typeCours: "",
  publicCible: "",
  capaciteMaximale: "",
    nombrePersonnesMaxAtelier: "",
  prerequis: "",
  materiel: "",
  accessiblePMR: false,
  pmrDetails: "",
  lieu: "",
  categories: [],
  autresCategories: [],
  autreCategorie: "",
  creditsProposes: "",
  dateAConvenir: false,
});


  const [imageSlots, setImageSlots] = useState(createEmptyImageSlots);
  const [creneaux, setCreneaux] = useState([createEmptyCreneau()]);
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasExistingReservations, setHasExistingReservations] = useState(false);
  const [initialLockedValues, setInitialLockedValues] = useState(null);

  // Charger le service au montage
  useEffect(() => {
    const fetchService = async () => {
      try {
        const token = localStorage.getItem("token");
        const [serviceRes, countRes] = await Promise.all([
          axios.get(`https://backend-qybe.onrender.com/api/services/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`https://backend-qybe.onrender.com/api/reservations/count/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => ({ data: { count: 0 } })),
        ]);
        const service = serviceRes.data;

        const loadedFormData = {
  titre: service.titre || "",
  description: service.description || "",
  typePrestation: service.typePrestation || "",
  nombrePlaces: service.nombrePlaces || "",
  ville: String(service.lieu || "").split(" - ")[0] || "",
  adresse: String(service.lieu || "").split(" - ").slice(1).join(" - ") || "",
  creditsProposes: service.creditsProposes?.toString() || "",
  dateService: [],

  heure: service.heure || "",
  duree: service.duree || "",
  typeCours: service.typeCours || "",
  publicCible: service.publicCible || "",
  capaciteMaximale: service.capaciteMaximale?.toString() || "",
  nombrePersonnesMaxAtelier: service.nombrePersonnesMaxAtelier?.toString() || "",
  prerequis: service.prerequis || "",
  materiel: service.materiel || "",
  accessiblePMR: service.accessiblePMR || false,
  pmrDetails: service.pmrDetails || "",
  lieu: service.lieu || "",
  categories: service.categories || [],
  autresCategories: Array.isArray(service.autresCategories) ? service.autresCategories : [],
  autreCategorie: "",
  dateAConvenir: service.dateAConvenir || false,
};

        setFormData(loadedFormData);
        setInitialLockedValues(loadedFormData);
        setHasExistingReservations((countRes.data?.count || 0) > 0);

        const mappedCreneaux = Array.isArray(service.dateService)
          ? service.dateService
              .map((d) => {
                const parsed = new Date(d);
                if (Number.isNaN(parsed.getTime())) return null;
                const date = parsed.toISOString().slice(0, 10);
                const heure = parsed.toTimeString().slice(0, 5);
                return { date, heure };
              })
              .filter(Boolean)
          : [];

        setCreneaux(mappedCreneaux.length > 0 ? mappedCreneaux : [createEmptyCreneau()]);

        const normalizedExistingImages = (service.images || [])
          .map(extractImageUrl)
          .filter(Boolean);
        setImageSlots(createImageSlotsFromExisting(normalizedExistingImages));

      } catch (err) {
        console.error("Erreur chargement service :", err);
        setMessage("❌ Impossible de charger ce service.");
      } finally {
        setIsLoading(false);
      }
    };

    if (id) fetchService();
  }, [id]);

  const handleImageSlotChange = (slotIndex, e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const previewUrl = URL.createObjectURL(selectedFile);

    setImageSlots((prev) => {
      const next = [...prev];
      const currentSlot = next[slotIndex];

      if (currentSlot?.newPreviewUrl) {
        URL.revokeObjectURL(currentSlot.newPreviewUrl);
      }

      next[slotIndex] = {
        ...currentSlot,
        newFile: selectedFile,
        newPreviewUrl: previewUrl,
      };
      return next;
    });

    // Permet de re-selectionner le meme fichier si besoin.
    e.target.value = "";
  };

  const handleRemoveImageSlot = (slotIndex) => {
    setImageSlots((prev) => {
      const next = [...prev];
      const currentSlot = next[slotIndex];

      if (currentSlot?.newPreviewUrl) {
        URL.revokeObjectURL(currentSlot.newPreviewUrl);
      }

      next[slotIndex] = {
        existingUrl: null,
        newFile: null,
        newPreviewUrl: null,
      };

      return next;
    });
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);
  setMessage("");

  let token = localStorage.getItem("token");
  const refreshToken = localStorage.getItem("refreshToken");

  if (!token) {
    setMessage("❌ Vous devez être connecté.");
    setIsSubmitting(false);
    return;
  }

  // Refresh token
  try {
    const decoded = JSON.parse(atob(token.split('.')[1]));
    const now = Math.floor(Date.now() / 1000);
    if (decoded.exp < now && refreshToken) {
      const refreshRes = await axios.post(
        "https://backend-qybe.onrender.com/api/auth/refreshToken",
        { refreshToken }
      );
      token = refreshRes.data.accessToken;
      localStorage.setItem("token", token);
    }
  } catch {
    setMessage("❌ Session expirée.");
    setIsSubmitting(false);
    return;
  }

  const initialMaxAtelier = Number(initialLockedValues?.nombrePersonnesMaxAtelier || 0);
  const requestedMaxAtelier = Number(formData.nombrePersonnesMaxAtelier || 0);

  if (
    hasExistingReservations &&
    formData.typeCours === "Collective" &&
    requestedMaxAtelier > 0 &&
    requestedMaxAtelier < initialMaxAtelier
  ) {
    setMessage("❌ Impossible de réduire le nombre de places max par atelier sur un service déjà réservé.");
    setIsSubmitting(false);
    return;
  }

  const effectiveFormData =
    hasExistingReservations && initialLockedValues
      ? {
          ...formData,
          ...Object.fromEntries(
            LOCKED_FIELDS_WHEN_RESERVED.map((key) => [key, initialLockedValues[key]])
          ),
        }
      : formData;

  const creditsNumber = parseInt(effectiveFormData.creditsProposes, 10);
  if (isNaN(creditsNumber) || creditsNumber <= 0) {
    setMessage("❌ Crédits invalides.");
    setIsSubmitting(false);
    return;
  }

  const validCreneaux = creneaux.filter((c) => c.date && c.heure).slice(0, 1);
  if (!effectiveFormData.dateAConvenir && validCreneaux.length === 0) {
    setMessage("❌ Ajoutez au moins un créneau avec date et heure.");
    setIsSubmitting(false);
    return;
  }

  if (!effectiveFormData.dateAConvenir && validCreneaux[0]) {
    const selectedDateTime = new Date(`${validCreneaux[0].date}T${validCreneaux[0].heure}:00`);
    if (selectedDateTime.getTime() < Date.now()) {
      setMessage("❌ La date du créneau ne peut pas être dans le passé.");
      setIsSubmitting(false);
      return;
    }
  }

  const safeFormData = {
    ...effectiveFormData,
    dateService: effectiveFormData.dateAConvenir
      ? []
      : [`${validCreneaux[0].date}T${validCreneaux[0].heure}:00`],
    heure: effectiveFormData.dateAConvenir ? "" : (validCreneaux[0]?.heure || ""),
  };

  let finalCategories = [...(safeFormData.categories || []).filter((category) => category !== "Autres")];
  const customCategories = (safeFormData.autresCategories || []).map((value) => value.trim()).filter(Boolean);
  finalCategories.push(...customCategories);
  finalCategories = [...new Set(finalCategories)].slice(0, 10);

  const data = new FormData();

  Object.entries(safeFormData).forEach(([key, value]) => {
    if (["categories", "autresCategories", "autreCategorie", "creditsProposes", "ville", "adresse"].includes(key)) return;

    if (Array.isArray(value)) {
      value.forEach(v => data.append(key, v));
    } else {
      data.append(key, value);
    }
  });

  data.append("creditsProposes", creditsNumber);
  data.append("lieu", [safeFormData.ville, safeFormData.adresse].filter(Boolean).join(" - "));
  data.append("accessiblePMR", safeFormData.accessiblePMR === true);
  data.append("dateAConvenir", safeFormData.dateAConvenir);
if (!safeFormData.dateAConvenir) {
  data.append("creneaux", JSON.stringify(validCreneaux));
}

  data.append("prerequis", safeFormData.prerequis);
  data.append("materiel", safeFormData.materiel);




  finalCategories.forEach(cat => data.append("categories", cat));
  
  // Reconstruire la liste finale depuis les 4 slots:
  // - garder les anciennes URLs des slots non remplaces
  // - uploader les nouveaux fichiers des slots modifies
  const keptExistingUrls = [];
  const newFilesToUpload = [];

  imageSlots.forEach((slot) => {
    if (slot.newFile) {
      newFilesToUpload.push(slot.newFile);
      return;
    }

    if (slot.existingUrl) {
      keptExistingUrls.push(slot.existingUrl);
    }
  });

  keptExistingUrls.forEach((url) => {
    data.append("imagesLinks", url);
  });

  // Compatibilite backend: certains handlers attendent image, d'autres images.
  newFilesToUpload.forEach((img) => {
    data.append("image", img);
  });

  try {
    await axios.put(
      `https://backend-qybe.onrender.com/api/services/${id}`,
      data,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    setMessage("✅ Service mis à jour avec succès !");
    setTimeout(() => navigate("/dashboard"), 1500);
  } catch (error) {
  console.error("❌ Erreur update service COMPLETE :", error);

  if (error.response) {
    console.error("📩 DATA :", error.response.data);
    console.error("📌 STATUS :", error.response.status);
    console.error("📎 HEADERS :", error.response.headers);
  } else if (error.request) {
    console.error("🚫 Aucune réponse du serveur :", error.request);
  } else {
    console.error("⚠️ Erreur JS :", error.message);
  }
}

   finally {
    setIsSubmitting(false);
  }
};



  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-lg text-gray-600">Chargement du service...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--app-background)' }}>
      {/* Header */}
      <Header />

      {/* Bandeau jaune décoratif */}
      <div className="bg-[var(--accent-primary-soft)] py-4">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-2xl font-bold text-[var(--accent-primary)]">Modifier votre service</h1>
          <p className="mt-1 text-[var(--accent-primary)]">Mettez à jour les détails de votre service</p>
        </div>
      </div>

      {/* Formulaire */}
      <main className="py-8 px-4">
        <div className="max-w-3xl mx-auto bg-white shadow-lg rounded-2xl p-8">

          {message && (
            <div
              className={`text-center font-semibold py-3 rounded-lg mb-6 ${
                message.startsWith("✅")
                  ? "bg-[var(--status-success-soft)] text-[var(--status-success-strong)]"
                  : "bg-red-100 text-red-700"
              }`}
            >
              {message}
            </div>
          )}

          {hasExistingReservations && (
            <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Service déjà réservé: seuls Titre, Description, Catégories, Photos et le nombre max par atelier (uniquement à la hausse) sont modifiables.
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <ServiceFormFields
              formData={formData}
              setFormData={setFormData}
              creneaux={creneaux}
              setCreneaux={setCreneaux}
              createEmptyCreneau={createEmptyCreneau}
              imageSlots={imageSlots}
              onImageSlotChange={handleImageSlotChange}
              onImageSlotRemove={handleRemoveImageSlot}
              maxImages={MAX_SERVICE_IMAGES}
              imageInputPrefix="edit-image-slot"
              isFieldEditable={(fieldName) => {
                if (!hasExistingReservations) return true;
                const allowedWhenReserved = new Set([
                  "titre",
                  "description",
                  "categories",
                  "autreCategorie",
                  "images",
                  "nombrePersonnesMaxAtelier",
                ]);
                return allowedWhenReserved.has(fieldName);
              }}
              minNombrePersonnesMaxAtelier={Number(initialLockedValues?.nombrePersonnesMaxAtelier || 1)}
            />

            {/* Boutons */}
            <div className="flex justify-between mt-6">
              <button
                type="button"
                onClick={() => navigate("/profile")}
                className="px-5 py-2.5 text-[var(--button-primary)] hover:text-[var(--button-primary-hover)] font-medium rounded-lg transition"
              >
                ← Annuler
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary disabled:bg-gray-400 font-semibold py-2.5 px-6"
              >
                {isSubmitting ? "Enregistrement..." : "💾 Enregistrer les modifications"}
              </button>
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default EditService;
